import { useEffect, useState } from "react";
import { api, apiError } from "@/lib/api";
import { useAtlas } from "@/context/AtlasContext";
import { PageHeader } from "@/components/shared/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { toast } from "sonner";
import { AlertTriangle } from "lucide-react";

export default function Company() {
  const { has } = useAtlas();
  const [name, setName] = useState("");
  const [tenant, setTenant] = useState(null);
  const [resetOpen, setResetOpen] = useState(false);
  const [confirm, setConfirm] = useState("");
  const [resetting, setResetting] = useState(false);
  const [purgeOpen, setPurgeOpen] = useState(false);
  const [purgeConfirm, setPurgeConfirm] = useState("");
  const [keepName, setKeepName] = useState("");
  const [purging, setPurging] = useState(false);
  const canManage = has("company.manage");
  const canReset = has("platform.reset");
  useEffect(() => { api.get("/admin/company").then((r) => { setTenant(r.data); setName(r.data.name); }).catch(() => {}); }, []);
  const save = async () => {
    try { await api.put("/admin/company", { name, logo: tenant?.logo, settings: tenant?.settings || {} }); toast.success("Company updated"); }
    catch (e) { toast.error(apiError(e)); }
  };
  const doReset = async () => {
    setResetting(true);
    try {
      const { data } = await api.post("/admin/reset-business-data", { confirm: "RESET" });
      const n = Object.values(data.report || {}).reduce((a, b) => a + (typeof b === "number" ? b : 0), 0);
      toast.success(`Workspace reset — ${n} sample records cleared`);
      setResetOpen(false); setConfirm("");
      setTimeout(() => window.location.reload(), 1200);
    } catch (e) { toast.error(apiError(e)); } finally { setResetting(false); }
  };
  const doPurge = async () => {
    setPurging(true);
    try {
      const { data } = await api.post("/admin/purge-except-client", { confirm: "PURGE", keep_client_name: keepName.trim() });
      const n = Object.entries(data.report || {}).filter(([k]) => !k.startsWith("kept")).reduce((a, [, b]) => a + (typeof b === "number" ? b : 0), 0);
      toast.success(`Kept "${data.report?.kept_org}" — ${n} other records removed`);
      setPurgeOpen(false); setPurgeConfirm("");
      setTimeout(() => window.location.reload(), 1200);
    } catch (e) { toast.error(apiError(e)); } finally { setPurging(false); }
  };
  if (!tenant) return <div className="text-slate-400">Loading…</div>;
  return (
    <div className="max-w-xl">
      <PageHeader title="Company" subtitle="Tenant identity & settings" />
      <div className="bg-white border border-slate-200 rounded-xl p-6 space-y-4">
        <div className="space-y-1.5"><Label>Company name</Label><Input value={name} onChange={(e) => setName(e.target.value)} disabled={!canManage} data-testid="company-name-input" /></div>
        <div className="space-y-1.5"><Label>Slug</Label><Input value={tenant.slug} disabled className="font-mono" /></div>
        <div className="space-y-1.5"><Label>Status</Label><Input value={tenant.status} disabled className="capitalize" /></div>
        {canManage && <Button onClick={save} data-testid="company-save-button">Save changes</Button>}
      </div>

      {canReset && (
        <div className="mt-8 border border-red-200 bg-red-50/50 rounded-xl p-6" data-testid="danger-zone">
          <h3 className="text-sm font-semibold text-red-700 flex items-center gap-2"><AlertTriangle className="w-4 h-4" /> Danger zone</h3>
          <p className="text-sm text-slate-600 mt-2">Reset all business data to start clean for go-live. This permanently removes every client, contact, opportunity, task, board, note, file and all sample/demo records, plus all other user logins and client portals. Your owner login, roles, pipeline and integration settings are kept.</p>
          <Button variant="outline" className="mt-3 border-red-300 text-red-700 hover:bg-red-100" onClick={() => setResetOpen(true)} data-testid="reset-data-open">Reset all business data…</Button>
          <div className="mt-5 pt-5 border-t border-red-200">
            <p className="text-sm font-medium text-red-700">Keep only one client, remove everything else</p>
            <p className="text-sm text-slate-600 mt-1">Removes every other organization, client, user and <b>all people</b> (including hidden/orphaned records) and all inventory — keeping your owner login plus the one client you name (its workspace, people and portal users). Use this to scrub leftover dummy people while keeping a real client.</p>
            <Button variant="outline" className="mt-3 border-red-300 text-red-700 hover:bg-red-100" onClick={() => setPurgeOpen(true)} data-testid="purge-open">Keep only one client…</Button>
          </div>
        </div>
      )}

      <Dialog open={purgeOpen} onOpenChange={setPurgeOpen}><DialogContent data-testid="purge-dialog">
        <DialogHeader><DialogTitle className="text-red-700">Keep only one client?</DialogTitle><DialogDescription>This cannot be undone. Everything except the client you name (and your owner login) is permanently deleted — all other orgs, users, people, tasks, boards, files and inventory. Enter the client name, then type <b>PURGE</b> to confirm.</DialogDescription></DialogHeader>
        <div className="space-y-2">
          <Input value={keepName} onChange={(e) => setKeepName(e.target.value)} placeholder="Client to keep (e.g. WrightWay)" data-testid="purge-keep-input" />
          <Input value={purgeConfirm} onChange={(e) => setPurgeConfirm(e.target.value)} placeholder="Type PURGE" data-testid="purge-confirm-input" />
        </div>
        <div className="flex justify-end gap-2">
          <Button variant="ghost" onClick={() => setPurgeOpen(false)}>Cancel</Button>
          <Button className="bg-red-600 hover:bg-red-700 text-white" disabled={purgeConfirm !== "PURGE" || !keepName.trim() || purging} onClick={doPurge} data-testid="purge-confirm-btn">{purging ? "Purging…" : "Keep client, remove the rest"}</Button>
        </div>
      </DialogContent></Dialog>

      <Dialog open={resetOpen} onOpenChange={setResetOpen}><DialogContent data-testid="reset-data-dialog">
        <DialogHeader><DialogTitle className="text-red-700">Reset all business data?</DialogTitle><DialogDescription>This cannot be undone. All clients, deals, tasks, boards, notes, files, client portals and other logins will be permanently deleted. Type <b>RESET</b> to confirm.</DialogDescription></DialogHeader>
        <Input value={confirm} onChange={(e) => setConfirm(e.target.value)} placeholder="Type RESET" data-testid="reset-confirm-input" />
        <div className="flex justify-end gap-2">
          <Button variant="ghost" onClick={() => setResetOpen(false)}>Cancel</Button>
          <Button className="bg-red-600 hover:bg-red-700 text-white" disabled={confirm !== "RESET" || resetting} onClick={doReset} data-testid="reset-confirm-btn">{resetting ? "Resetting…" : "Permanently reset"}</Button>
        </div>
      </DialogContent></Dialog>
    </div>
  );
}
